import { useLocation, Navigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import { useState, useEffect } from 'react';
import { authenticatedPostRequest } from '../RestApi';
import { Skeleton } from '@mui/material';
import { useSubscriptionStatus } from '../Contexts/Contexts';


export function RequireAuthN({ onFailureRoute = '/login', children }) {
	const location = useLocation();
	// const { route, user } = useAuthenticator((context) => [context.route, context.user]);
	const { authStatus, user } = useAuthenticator((context) => [context.authStatus, context.user]);

	if (authStatus === 'configuring') {
		return null;
	}

	if (authStatus !== 'authenticated') {
		// Use Navigate to redirect to the failure route
		// Preserve the current location in the state and add 'from' parameter to the search		
		return <Navigate 
			to={{
				pathname: onFailureRoute,
				search: location.search,
			}} 
			replace 
		/>;
	}

	// console.log(user);
	// if (user && isValidInvitationCode(user.attributes['sub'], user.attributes['customer:invitation_code'])) {
	//   return children;
	// }

	return children;
}

//@@@TODO RquireAuthZ is not yet implemented

// export function RequireAuthZ({ children }) {
// 	const location = useLocation();
// 	const { route, user } = useAuthenticator((context) => [context.route, context.user]);

// 	// console.log("RequireAuth", location);
// 	// console.log("RequireAuth", route);

// 	if (route === 'idle') {
// 		return null;
// 	}

// 	if (route !== 'authenticated') {
// 		return <Navigate to="/login" state={{ from: location }} replace />;
// 	}

// 	// console.log(user);
// 	// if (user && isValidInvitationCode(user.attributes['sub'], user.attributes['customer:invitation_code'])) {
// 	//   return children;
// 	// }
// 	return children;
// }

export function RequireSubscription({ children, group = null, noRedirect = false }) {
	const location = useLocation();
	// const { route, user } = useAuthenticator((context) => [context.route, context.user]);
	const { authStatus, user } = useAuthenticator((context) => [context.authStatus, context.user]);
	// Use global subscription status context
	const [subscriptionStatus, setSubscriptionStatus] = useSubscriptionStatus();

	// So we can show skeletons until the subscription status endpoint comes back:
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		const checkSubscription = async () => {
			try {
				const status = await getSubscriptionStatus(group);
				console.log("Subscription status", status, group);
				setSubscriptionStatus(status);
			} catch (error) {
				// Inactive by default:
				console.log(error);
				setSubscriptionStatus("inactive");
			} finally {
				// So we can turn the skeletons off and return children:
				setIsLoading(false);
			}
		};

		if (authStatus === 'authenticated' && user) {
			checkSubscription();
		} else {
			// Turn on skeletons until we're done checking:
			setIsLoading(false);
		}
	}, [authStatus, user, group, setSubscriptionStatus]);

	if (!noRedirect && (authStatus === 'configuring' || isLoading || (authStatus === 'authenticated' && subscriptionStatus === null))) {
		return (
			<div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
				<Skeleton variant="rectangular" height={60} style={{ marginBottom: '20px' }} />
				<Skeleton variant="rectangular" height={200} />
			</div>
		);
	}

	if (authStatus !== 'authenticated') {
		if (noRedirect) {
			return null;
		}
		return <Navigate 
			to={{
				pathname: "/login",
				search: location.search,
			}} 
			replace 
		/>;
	}

    // If they're not in group, let's send them to the pricing page
    if (subscriptionStatus === 'not_in_group') {
        if (noRedirect) {
            return null;
        }
        console.log('Redirecting to pricing page...', subscriptionStatus, authStatus, user);
        window.location.href = `${process.env.REACT_APP_WWW_URL}/pricing${location.search || ''}`;
        return null;
    }

	// If they aren't actively subscribed or in trialing mode, send them 
	// to the subscription page:
	if (subscriptionStatus !== 'active' && subscriptionStatus !== 'trialing') {
		if (noRedirect) {
			return null;
		}
		console.log('Redirecting to subscription page...', subscriptionStatus, authStatus, user);
		return <Navigate to={{pathname: "/subscribe", search: `${location.search}&from=${location.pathname}`}} replace/>;		
	}

	return children;
}

export async function getSubscriptionStatus(group = null) {
	try {
		const user = await Auth.currentAuthenticatedUser();
		if (process.env.NODE_ENV === 'development') {
			console.log("Checking subscription for user", user);
		}

		const response = await authenticatedPostRequest(
			`${process.env.REACT_APP_SUBSCRIPTION_API_URL}/subscription_status`,
			{
				'cognitoSub': user.signInUserSession.idToken.payload.sub,  // Cannot use user.attributes.sub because Google sign-ups don't have attributes
			}
		);
		
		
		// If group is specified, check if user is in that group
		if (group && response['tagged_groups']) {
			if (!response['tagged_groups'].includes(group)) {
				return 'not_in_group';
			}
		}
		
		return response['status'];
	} catch (error) {
		console.log(error);
		return "inactive";
	}
}

export function ViewAsDemoUser({ children }) {
	const { authStatus } = useAuthenticator((context) => [context.authStatus])

	useEffect(() => {
		async function checkAndLogin() {
			try {
				await Auth.currentAuthenticatedUser();
			} catch {
				// Not authenticated, try to sign in as demo user
				try {
					const username = process.env.REACT_APP_DEMO_USER_EMAIL;
					const password = process.env.REACT_APP_DEMO_USER_PASSWORD;
					await Auth.signIn(username, password);
				} catch (error) {
					console.error('Error signing in as demo user:', error);
				}
			}
		}

		checkAndLogin();

		function handlePageShow(event) {
			if (event.persisted) {
				window.location.reload();
			}
		}
		window.addEventListener('pageshow', handlePageShow);

		return () => {
			window.removeEventListener('pageshow', handlePageShow);
		};
	}, []);


	if (authStatus == 'authenticated') {
		return children;
	}

	return null;
}


export default RequireAuthN;

